/**
 * Legal copy + renderer for the Terms of Service / Privacy Notice shown to
 * users before they create an account.
 *
 * IMPORTANT: This is a plain-English template tailored to how the product
 * actually works (real-time two-speaker translation, voice enrollment, and
 * third-party AI processors). It is NOT legal advice. Have it reviewed by a
 * qualified lawyer — especially the EU/GDPR, biometric-data, governing-law,
 * and liability sections — before relying on it commercially.
 *
 * When you make a material change, bump TERMS_VERSION and EFFECTIVE_DATE so
 * consent can be re-collected and audited.
 */
import type { ReactNode } from "react";
import { Modal } from "./ui";

export const COMPANY = "Aevum Intelligence";
export const PRODUCT = "Aevum Translate";
export const EFFECTIVE_DATE = "20 June 2026";
export const TERMS_VERSION = "2026-06-20";
export const SUPPORT_EMAIL = "support@aevum-intel.com";
export const PRIVACY_EMAIL = "privacy@aevum-intel.com";

type Section = { id: string; heading: string; body: ReactNode };

const SECTIONS: Section[] = [
  {
    id: "intro",
    heading: "Summary",
    body: (
      <>
        <p>
          {PRODUCT} (the &ldquo;Service&rdquo;) is operated by {COMPANY}
          (&ldquo;we&rdquo;, &ldquo;us&rdquo;). It provides real-time, two-speaker
          voice translation. To do this we process your microphone audio,
          transcribe it, translate it, and synthesise spoken output. Some
          processing happens on trusted third-party AI services (see
          &ldquo;Sub-processors&rdquo;).
        </p>
        <p>
          By creating an account or using the Service you agree to these Terms
          and to the Privacy Notice below. If you do not agree, do not use the
          Service.
        </p>
      </>
    ),
  },
  {
    id: "eligibility",
    heading: "1. Eligibility",
    body: (
      <p>
        You must be at least 16 years old (or the age of digital consent in
        your country, whichever is higher) to use the Service. If you use it on
        behalf of an organisation, you confirm you are authorised to bind that
        organisation to these Terms.
      </p>
    ),
  },
  {
    id: "accounts",
    heading: "2. Your account",
    body: (
      <p>
        You are responsible for the credentials you choose and for activity
        under your account. Use a strong, unique password and keep it
        confidential. Tell us promptly at {SUPPORT_EMAIL} if you suspect
        unauthorised access.
      </p>
    ),
  },
  {
    id: "use",
    heading: "3. Acceptable use",
    body: (
      <>
        <p>You agree not to:</p>
        <ul>
          <li>use the Service unlawfully, or to harass, defraud, or harm others;</li>
          <li>
            record or translate other people without any consent that the law in
            your location requires — you are responsible for obtaining it;
          </li>
          <li>
            attempt to reverse-engineer, overload, scrape, or circumvent the
            security or rate limits of the Service;
          </li>
          <li>upload content you have no right to process.</li>
        </ul>
      </>
    ),
  },
  {
    id: "voice",
    heading: "4. Voice, recordings & voiceprints",
    body: (
      <>
        <p>
          The Service captures audio while a session is active. Optional voice
          enrollment creates a <strong>voiceprint</strong> — a mathematical
          model of your voice used to tell speakers apart. In the EU/EEA and UK
          a voiceprint is <strong>biometric data</strong> (a special category of
          personal data).
        </p>
        <p>
          By enrolling your voice you give your <strong>explicit consent</strong>{" "}
          to us processing your voiceprint for speaker identification within the
          Service. You can withdraw consent at any time by deleting your
          enrollment in Settings, which deletes the voiceprint.
        </p>
      </>
    ),
  },
  {
    id: "ai",
    heading: "5. AI translation & accuracy",
    body: (
      <>
        <p>
          Translation and speech features rely on automated machine-learning
          models. They can be wrong, incomplete, or misleading. The Service is
          provided for general communication only.
        </p>
        <p>
          <strong>
            Do not rely on it for emergency, medical, legal, financial, or other
            high-stakes decisions.
          </strong>{" "}
          Always verify anything important with a qualified human interpreter.
        </p>
        <p>
          The &ldquo;Try demo&rdquo; experience uses an AI stand-in for the
          second speaker; it is a simulation, not a real conversation.
        </p>
      </>
    ),
  },
  {
    id: "privacy",
    heading: "6. Privacy Notice (incl. GDPR)",
    body: (
      <>
        <p>
          This section explains how we handle personal data and applies to
          everyone, with additional rights for users in the EU/EEA and UK under
          the GDPR / UK GDPR. For these laws, {COMPANY} is the{" "}
          <strong>data controller</strong>.
        </p>
        <p><strong>What we process:</strong></p>
        <ul>
          <li>account data (email, display name, language and voice settings);</li>
          <li>session audio and the resulting transcripts &amp; translations;</li>
          <li>optional voiceprints (biometric data) if you enroll;</li>
          <li>basic technical/usage logs needed to run and secure the Service.</li>
        </ul>
        <p><strong>Why, and our legal basis:</strong></p>
        <ul>
          <li>to provide the Service you request — <em>performance of a contract</em>;</li>
          <li>to process voiceprints — your <em>explicit consent</em>;</li>
          <li>to keep the Service secure and reliable — our <em>legitimate interests</em>;</li>
          <li>to meet legal obligations where they apply.</li>
        </ul>
        <p>
          <strong>Retention:</strong> we keep account data while your account is
          active. Session content and voiceprints are kept only as long as
          needed for the feature you used and are deleted when you delete them or
          close your account, subject to short backup cycles.
        </p>
        <p>
          <strong>International transfers:</strong> our AI sub-processors may
          process data outside your country, including in the United States.
          Where required, such transfers rely on appropriate safeguards such as
          the EU Standard Contractual Clauses.
        </p>
        <p>
          <strong>Your rights (EU/EEA/UK):</strong> access, rectification,
          erasure, restriction, portability, objection, and the right to
          withdraw consent at any time without affecting prior processing. You
          may also lodge a complaint with your local supervisory authority. To
          exercise any right, email {PRIVACY_EMAIL}.
        </p>
      </>
    ),
  },
  {
    id: "subprocessors",
    heading: "7. Sub-processors",
    body: (
      <>
        <p>
          We use vetted third-party providers to deliver the Service — for
          example cloud hosting and infrastructure, and speech and translation
          AI processors. They process data only on our instructions under
          data-processing agreements with appropriate safeguards.
        </p>
        <p>
          We do not sell your personal data and do not use your private
          conversations to train our own models without a separate, clearly
          labelled opt-in. An up-to-date list of our sub-processors is available
          on request at {PRIVACY_EMAIL}.
        </p>
      </>
    ),
  },
  {
    id: "ip",
    heading: "8. Intellectual property",
    body: (
      <p>
        The Service, its software, and branding belong to {COMPANY}. You keep
        ownership of the content you provide; you grant us the limited licence
        needed to process it solely to operate and improve the Service as
        described here.
      </p>
    ),
  },
  {
    id: "warranty",
    heading: "9. Disclaimers",
    body: (
      <p>
        The Service is provided &ldquo;as is&rdquo; and &ldquo;as
        available&rdquo;, without warranties of any kind to the maximum extent
        permitted by law. We do not warrant that translations are accurate or
        that the Service will be uninterrupted or error-free. Nothing here
        excludes rights that cannot be excluded under the law that applies to
        you, including mandatory consumer-protection rights in the EU/EEA and
        UK.
      </p>
    ),
  },
  {
    id: "liability",
    heading: "10. Limitation of liability",
    body: (
      <p>
        To the maximum extent permitted by law, {COMPANY} is not liable for
        indirect, incidental, special, or consequential damages, or for loss
        arising from reliance on a translation. Where liability cannot be
        excluded, it is limited to the amount you paid for the Service in the 12
        months before the claim. Nothing limits liability for death or personal
        injury caused by negligence, fraud, or anything else that cannot be
        limited by law.
      </p>
    ),
  },
  {
    id: "termination",
    heading: "11. Termination",
    body: (
      <p>
        You may stop using the Service and delete your account at any time. We
        may suspend or end access if you breach these Terms or to protect the
        Service or other users. You can request deletion of your data as
        described in the Privacy Notice.
      </p>
    ),
  },
  {
    id: "changes",
    heading: "12. Changes",
    body: (
      <p>
        We may update these Terms. If a change is material we will take
        reasonable steps to notify you and, where required, ask you to accept
        the new version. Continued use after an update means you accept it.
      </p>
    ),
  },
  {
    id: "law",
    heading: "13. Governing law & contact",
    body: (
      <p>
        These Terms are governed by the laws applicable at {COMPANY}&rsquo;s
        place of establishment, without limiting mandatory consumer rights in
        your country of residence. Questions? Contact {SUPPORT_EMAIL} (general)
        or {PRIVACY_EMAIL} (privacy &amp; data requests).
      </p>
    ),
  },
];

/** Full Terms of Service + Privacy Notice, rendered as scrollable content. */
export function LegalContent() {
  return (
    <div
      style={{
        padding: "4px 22px 24px",
        fontFamily: "var(--font-ui)",
        fontSize: 14,
        lineHeight: 1.6,
        color: "var(--ink-2)",
      }}
    >
      <p style={{ fontSize: 12.5, color: "var(--ink-3)", marginTop: 0 }}>
        Effective {EFFECTIVE_DATE} · Version {TERMS_VERSION}
      </p>
      {SECTIONS.map((s) => (
        <section key={s.id} style={{ marginTop: 18 }}>
          <h3
            style={{
              fontSize: 15.5,
              fontWeight: 700,
              color: "var(--ink)",
              margin: "0 0 6px",
            }}
          >
            {s.heading}
          </h3>
          <div className="legal-body">{s.body}</div>
        </section>
      ))}
    </div>
  );
}

/** Modal wrapper that shows {@link LegalContent}. */
export function LegalModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Terms of Service & Privacy Notice"
      sub={`${COMPANY} · ${PRODUCT}`}
      width={620}
    >
      <LegalContent />
    </Modal>
  );
}
